import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    motion,
    useScroll,
    useSpring,
    AnimatePresence,
} from "motion/react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiArrowLeft,
    FiEye,
    FiCalendar,
    FiClock,
    FiArrowRight,
    FiShare2,
    FiCheck,
    FiBookOpen,
    FiList,
    FiZap,
    FiTrendingUp,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import SmartImage from "../components/Client/SmartImage";
import ClientAdSense from "../components/Client/ClientAdSense";
import ClientCalling from "../api/ClientCalling";

const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const formatViews = (v) => {
    if (!v) return "0";
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
    if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
    return v.toString();
};

const calculateReadTime = (description, subInfo) => {
    let words = description?.split(/\s+/).length || 0;
    if (subInfo) {
        subInfo.forEach((s) => {
            words += s?.sub_desciption?.split(/\s+/).length || 0;
        });
    }
    return Math.max(1, Math.ceil(words / 200));
};

const slugify = (str, i) =>
    `section-${i}-${(str || "")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40) || "x"}`;

const ClientBlogInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);

    const articleRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: articleRef,
        offset: ["start start", "end end"],
    });
    const progress = useSpring(scrollYProgress, {
        stiffness: 200,
        damping: 30,
        mass: 0.4,
    });

    useEffect(() => {
        if (id) fetchBlog();
    }, [id]);

    const fetchBlog = async () => {
        setLoading(true);
        setNotFound(false);
        try {
            const resp = await ClientCalling.getBlogByTitle(id);
            if (resp?.status && resp?.data) setBlog(resp.data);
            else setNotFound(true);
        } catch (err) {
            console.error("Blog info fetch failed:", err);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const sections = useMemo(
        () =>
            Array.isArray(blog?.sub_info)
                ? blog.sub_info.map((s, i) => ({
                      ...s,
                      _id: slugify(s.sub_title, i),
                      _num: i + 1,
                  }))
                : [],
        [blog]
    );

    const readTime = useMemo(
        () =>
            blog ? calculateReadTime(blog.main_description, blog.sub_info) : 0,
        [blog]
    );

    // Track active section
    useEffect(() => {
        if (sections.length === 0) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        const idx = sections.findIndex((s) => s._id === e.target.id);
                        if (idx >= 0) setActiveIdx(idx);
                    }
                });
            },
            { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
        );
        sections.forEach((s) => {
            const el = document.getElementById(s._id);
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, [sections]);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleShare = async () => {
        const url = typeof window !== "undefined" ? window.location.href : "";
        try {
            if (navigator.share) {
                await navigator.share({
                    title: blog?.main_title || "PicPromptify Blog",
                    url,
                });
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
            }
        } catch {
            /* user cancelled */
        }
    };

    // ===== Loading =====
    if (loading) {
        return (
            <div className='min-h-screen dm-sans client-home-bg'>
                <ClientNavbar />
                <FixedButton />
                <div className='mx-auto max-w-5xl px-4 sm:px-6 md:px-8 pt-6 md:pt-10 pb-16'>
                    <div className='h-9 w-32 ios-shimmer rounded-full mb-6' />
                    <div className='h-72 md:h-96 ios-shimmer rounded-3xl mb-6' />
                    <div className='space-y-3'>
                        <div className='h-3 w-24 ios-shimmer rounded-full' />
                        <div className='h-10 ios-shimmer rounded-md w-11/12' />
                        <div className='h-10 ios-shimmer rounded-md w-3/4' />
                        <div className='flex gap-2 pt-2'>
                            <div className='h-8 w-32 ios-shimmer rounded-full' />
                            <div className='h-8 w-28 ios-shimmer rounded-full' />
                            <div className='h-8 w-28 ios-shimmer rounded-full' />
                        </div>
                        <div className='space-y-2 pt-6'>
                            <div className='h-4 ios-shimmer rounded-md w-full opacity-70' />
                            <div className='h-4 ios-shimmer rounded-md w-full opacity-70' />
                            <div className='h-4 ios-shimmer rounded-md w-4/5 opacity-70' />
                        </div>
                    </div>
                </div>
                <ClientFooter />
            </div>
        );
    }

    // ===== Not Found =====
    if (notFound || !blog) {
        return (
            <div className='min-h-screen dm-sans client-home-bg'>
                <ClientNavbar />
                <FixedButton />
                <div className='mx-auto max-w-3xl px-4 sm:px-6 md:px-8 py-20 md:py-28 text-center'>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        className='flex flex-col items-center'
                    >
                        <div
                            className='w-20 h-20 rounded-2xl flex items-center justify-center mb-5'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                border: "1px solid rgba(255,255,255,0.12)",
                                boxShadow:
                                    "0 12px 28px -10px rgba(127,0,255,0.45)",
                            }}
                        >
                            <FiBookOpen className='text-3xl text-client-title' />
                        </div>
                        <h2 className='text-2xl md:text-3xl font-bold text-client-font tracking-tight'>
                            Article not found
                        </h2>
                        <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                            The article you're looking for doesn't exist or has been removed.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => navigate("/blog")}
                            className='mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm'
                            style={{
                                background:
                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                boxShadow:
                                    "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                            }}
                        >
                            <FiArrowLeft />
                            Back to blog
                        </motion.button>
                    </motion.div>
                </div>
                <ClientFooter />
            </div>
        );
    }

    const isTrending = (blog.views || 0) >= 1000;

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* Reading progress bar */}
            <motion.div
                className='fixed top-0 left-0 right-0 h-[3px] z-[60] origin-left'
                style={{
                    scaleX: progress,
                    background:
                        "linear-gradient(90deg, #7F00FF, #FF8AFF, #FCFA02)",
                    boxShadow: "0 0 12px rgba(127,0,255,0.6)",
                }}
            />

            {/* HERO */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 40, -20, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -30, 30, 0], y: [0, 30, -30, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-20 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-5xl px-4 sm:px-6 md:px-8 pt-5 md:pt-8 pb-6 md:pb-10'>
                    {/* Back */}
                    <motion.button
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        whileHover={{ x: -4 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate("/blog")}
                        className='ios-glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs font-medium mb-5'
                    >
                        <FiArrowLeft className='text-sm' />
                        Back to blog
                    </motion.button>

                    {/* DSPY1 — top-of-article display ad, lazy-loaded */}
                    <ClientAdSense
                        slot='9729451986'
                        lazyLoad
                        containerClassName='w-full mb-6 md:mb-8'
                    />

                    {/* Eyebrow row */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.05 }}
                        className='flex flex-wrap items-center gap-2 mb-4'
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>Article</span>
                        </div>
                        {isTrending && (
                            <motion.div
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, type: "spring", stiffness: 280, damping: 18 }}
                                className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wider text-white'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow:
                                        "0 8px 20px -6px rgba(127,0,255,0.55)",
                                }}
                            >
                                <FiTrendingUp className='text-xs' />
                                Trending
                            </motion.div>
                        )}
                    </motion.div>

                    {/* Title with gradient highlight on last word */}
                    <motion.h1
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className='text-3xl md:text-5xl lg:text-[3.25rem] font-bold text-client-font tracking-tight leading-[1.1]'
                    >
                        {blog.main_title}
                    </motion.h1>

                    {/* Meta */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className='mt-5 flex flex-wrap items-center gap-2'
                    >
                        <div
                            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs'
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <FiCalendar className='text-[11px]' style={{ color: "#FCFA02" }} />
                            {formatDate(blog.createdAt)}
                        </div>
                        <div
                            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs'
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <FiClock className='text-[11px]' style={{ color: "#FCFA02" }} />
                            {readTime} min read
                        </div>
                        <div
                            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(127,0,255,0.05))",
                                border: "1px solid rgba(127,0,255,0.35)",
                            }}
                        >
                            <FiEye className='text-[11px]' style={{ color: "#FCFA02" }} />
                            <span className='tabular-nums font-bold text-client-title'>
                                {formatViews(blog.views)}
                            </span>
                            <span className='text-client-font/75'>views</span>
                        </div>

                        <motion.button
                            whileHover={{ y: -2, scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleShare}
                            className='ios-glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium ml-auto'
                        >
                            {copied ? (
                                <>
                                    <FiCheck style={{ color: "#FCFA02" }} />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <FiShare2 />
                                    Share
                                </>
                            )}
                        </motion.button>
                    </motion.div>

                    {/* Hero image */}
                    {blog.main_img && (
                        <motion.div
                            initial={{ opacity: 0, y: 24, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            className='relative mt-6 md:mt-8 rounded-3xl overflow-hidden group'
                            style={{
                                boxShadow:
                                    "0 30px 70px -22px rgba(127,0,255,0.55), 0 14px 32px -10px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.14)",
                                border: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <div className='relative w-full' style={{ aspectRatio: "16 / 9" }}>
                                <SmartImage
                                    src={blog.main_img}
                                    alt={blog.main_title}
                                    className='absolute inset-0 w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-[1.04]'
                                />
                                <div
                                    aria-hidden
                                    className='absolute inset-0 pointer-events-none'
                                    style={{
                                        background:
                                            "linear-gradient(to top, rgba(0,0,0,0.5), transparent 35%)",
                                    }}
                                />
                                {/* Glossy edge */}
                                <div
                                    aria-hidden
                                    className='absolute inset-x-0 top-0 h-1/3 pointer-events-none opacity-60'
                                    style={{
                                        background:
                                            "linear-gradient(to bottom, rgba(255,255,255,0.18), transparent)",
                                        mixBlendMode: "overlay",
                                    }}
                                />
                            </div>
                            {/* Floating quick-info pill on the hero image */}
                            <div
                                className='absolute left-4 bottom-4 md:left-6 md:bottom-6 inline-flex items-center gap-2 px-3 py-2 rounded-full'
                                style={{
                                    background: "rgba(18,18,18,0.65)",
                                    backdropFilter: "blur(14px) saturate(180%)",
                                    WebkitBackdropFilter: "blur(14px) saturate(180%)",
                                    border: "1px solid rgba(255,255,255,0.14)",
                                    boxShadow:
                                        "0 10px 26px -8px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
                                }}
                            >
                                <FiZap className='text-xs' style={{ color: "#FCFA02" }} />
                                <span className='text-white text-[11px] md:text-xs font-semibold'>
                                    PicPromptify Insights
                                </span>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* MAIN GRID: TOC + ARTICLE */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div
                    ref={articleRef}
                    className='mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10'
                >
                    {/* TOC sidebar */}
                    {sections.length > 0 && (
                        <aside className='lg:col-span-4 xl:col-span-3 lg:sticky lg:top-24 lg:self-start order-2 lg:order-1'>
                            <div
                                className='rounded-3xl p-4 md:p-5 relative overflow-hidden'
                                style={{
                                    background: "rgba(255,255,255,0.04)",
                                    backdropFilter: "blur(18px) saturate(180%)",
                                    WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    boxShadow:
                                        "0 14px 36px -16px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
                                }}
                            >
                                <div
                                    aria-hidden
                                    className='absolute -top-16 -right-12 w-44 h-44 rounded-full opacity-15 blur-3xl pointer-events-none'
                                    style={{
                                        background:
                                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                                    }}
                                />
                                <div className='relative flex items-center gap-2 mb-3'>
                                    <FiList style={{ color: "#FCFA02" }} className='text-sm' />
                                    <p className='text-client-title text-[10px] uppercase tracking-[0.2em] font-semibold'>
                                        In this article
                                    </p>
                                </div>
                                <nav className='relative space-y-1 max-h-[60vh] overflow-y-auto pr-1'>
                                    {sections.map((s, i) => {
                                        const active = activeIdx === i;
                                        return (
                                            <motion.button
                                                key={s._id}
                                                whileHover={{ x: 4 }}
                                                onClick={() => scrollTo(s._id)}
                                                className='relative w-full flex items-start gap-2 px-3 py-2 rounded-xl text-left transition-colors'
                                                style={{
                                                    background: active
                                                        ? "linear-gradient(135deg, rgba(127,0,255,0.28), rgba(252,250,2,0.06))"
                                                        : "transparent",
                                                    border: active
                                                        ? "1px solid rgba(127,0,255,0.42)"
                                                        : "1px solid transparent",
                                                    color: active
                                                        ? "#FCFA02"
                                                        : "rgba(255,255,255,0.65)",
                                                }}
                                            >
                                                <span
                                                    className='inline-flex w-5 h-5 rounded-md items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5'
                                                    style={{
                                                        background: active
                                                            ? "linear-gradient(135deg, #7F00FF, #FF8AFF)"
                                                            : "rgba(255,255,255,0.06)",
                                                        color: active ? "#fff" : "rgba(255,255,255,0.65)",
                                                    }}
                                                >
                                                    {i + 1}
                                                </span>
                                                <span className='text-[12px] md:text-[13px] font-medium flex-1 leading-snug'>
                                                    {s.sub_title}
                                                </span>
                                                {active && (
                                                    <FiArrowRight
                                                        className='text-xs flex-shrink-0 mt-1'
                                                        style={{ color: "#FCFA02" }}
                                                    />
                                                )}
                                            </motion.button>
                                        );
                                    })}
                                </nav>

                                {/* Read progress mini bar */}
                                <div className='relative mt-4 pt-3'
                                    style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                    <div className='flex items-center justify-between mb-1.5'>
                                        <span className='text-client-font/55 text-[10px] uppercase tracking-wider font-semibold'>
                                            Progress
                                        </span>
                                        <span className='text-client-title text-[10px] tabular-nums font-bold'>
                                            {activeIdx + 1}/{sections.length}
                                        </span>
                                    </div>
                                    <div
                                        className='h-1.5 rounded-full overflow-hidden'
                                        style={{ background: "rgba(255,255,255,0.06)" }}
                                    >
                                        <motion.div
                                            className='h-full rounded-full origin-left'
                                            style={{
                                                scaleX: progress,
                                                background:
                                                    "linear-gradient(90deg, #7F00FF, #FF8AFF, #FCFA02)",
                                                width: "100%",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Share button in TOC */}
                                <motion.button
                                    whileHover={{ y: -2, scale: 1.02 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={handleShare}
                                    className='relative mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-white text-xs font-semibold'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        boxShadow:
                                            "0 10px 24px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
                                    }}
                                >
                                    {copied ? (
                                        <>
                                            <FiCheck />
                                            Link copied
                                        </>
                                    ) : (
                                        <>
                                            <FiShare2 />
                                            Share article
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </aside>
                    )}

                    {/* Article */}
                    <article
                        className={`${
                            sections.length > 0
                                ? "lg:col-span-8 xl:col-span-9 order-1 lg:order-2"
                                : "lg:col-span-12"
                        } space-y-5 md:space-y-6`}
                    >
                        {/* Overview */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5 }}
                            className='ios-glass-dark relative rounded-3xl p-5 md:p-8 overflow-hidden'
                        >
                            <div
                                aria-hidden
                                className='absolute -top-16 -right-12 w-44 h-44 rounded-full opacity-20 blur-3xl pointer-events-none'
                                style={{
                                    background:
                                        "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                                }}
                            />
                            <div className='relative flex items-start gap-3 mb-4'>
                                <div
                                    className='w-1 h-7 rounded-full flex-shrink-0'
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                    }}
                                />
                                <div>
                                    <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold'>
                                        Overview
                                    </p>
                                    <p className='text-client-font/85 font-semibold text-sm md:text-base'>
                                        The key takeaways
                                    </p>
                                </div>
                            </div>
                            <p
                                className='relative text-client-font/80 text-base md:text-[17px] leading-relaxed whitespace-pre-wrap first-letter:text-3xl first-letter:font-bold first-letter:mr-1 first-letter:float-left first-letter:leading-none first-letter:text-client-title'
                                style={{ wordBreak: "break-word" }}
                            >
                                {blog.main_description}
                            </p>

                            {blog.cta && (
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => window.open(blog.cta, "_blank")}
                                    className='relative inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full text-white font-semibold text-sm'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        boxShadow:
                                            "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                                    }}
                                >
                                    Click Here
                                    <FiArrowRight />
                                </motion.button>
                            )}
                        </motion.div>

                        {/* Sub sections */}
                        {sections.length > 0 && (
                            <div className='space-y-4 md:space-y-5'>
                                {sections.map((section, i) => (
                                    <motion.div
                                        key={section._id}
                                        id={section._id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.12 }}
                                        transition={{
                                            duration: 0.5,
                                            delay: Math.min(i * 0.04, 0.3),
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                        className='ios-glass-dark relative rounded-3xl p-5 md:p-7 overflow-hidden scroll-mt-24'
                                        style={{
                                            boxShadow:
                                                "0 14px 36px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.1)",
                                        }}
                                    >
                                        <div
                                            aria-hidden
                                            className='absolute -top-12 -right-10 w-40 h-40 rounded-full opacity-15 blur-3xl pointer-events-none'
                                            style={{
                                                background: `radial-gradient(circle, ${
                                                    i % 2 === 0 ? "#7F00FF" : "#FCFA02"
                                                } 0%, transparent 70%)`,
                                            }}
                                        />
                                        <div className='relative flex items-start gap-3 mb-3'>
                                            <motion.div
                                                whileHover={{ rotate: 8, scale: 1.05 }}
                                                transition={{ type: "spring", stiffness: 320, damping: 14 }}
                                                className='w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 font-bold tabular-nums text-white text-base md:text-lg'
                                                style={{
                                                    background:
                                                        i % 2 === 0
                                                            ? "linear-gradient(135deg, #7F00FF, #FF8AFF)"
                                                            : "linear-gradient(135deg, #FCFA02, #FF8AFF)",
                                                    boxShadow:
                                                        i % 2 === 0
                                                            ? "0 8px 22px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.22)"
                                                            : "0 8px 22px -6px rgba(252,250,2,0.5), inset 0 1px 0 rgba(255,255,255,0.22)",
                                                }}
                                            >
                                                {i + 1}
                                            </motion.div>
                                            <div className='flex-1 min-w-0 pt-1'>
                                                <p className='text-client-title text-[9px] md:text-[10px] uppercase tracking-[0.18em] font-semibold mb-1'>
                                                    Section {i + 1} of {sections.length}
                                                </p>
                                                <h3 className='text-client-font font-bold text-lg md:text-2xl tracking-tight leading-tight'>
                                                    {section.sub_title}
                                                </h3>
                                            </div>
                                        </div>
                                        {section.sub_desciption && (
                                            <p
                                                className='relative text-client-font/75 text-sm md:text-[15px] leading-relaxed whitespace-pre-wrap pl-[52px] md:pl-[60px]'
                                                style={{ wordBreak: "break-word" }}
                                            >
                                                {section.sub_desciption}
                                            </p>
                                        )}
                                    </motion.div>
                                ))}

                                {/* End decoration */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    className='flex flex-col items-center gap-2 py-8 text-client-font/40'
                                >
                                    <HiSparkles
                                        className='text-2xl'
                                        style={{ color: "#7F00FF" }}
                                    />
                                    <p className='text-[10px] md:text-xs tracking-[0.2em] uppercase font-semibold'>
                                        End of article
                                    </p>
                                </motion.div>
                            </div>
                        )}

                        {/* Big CTA card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className='relative rounded-3xl p-6 md:p-8 overflow-hidden text-center'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.22), rgba(252,250,2,0.06))",
                                border: "1px solid rgba(127,0,255,0.35)",
                                backdropFilter: "blur(18px) saturate(180%)",
                                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                boxShadow:
                                    "0 18px 40px -12px rgba(127,0,255,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
                            }}
                        >
                            <motion.div
                                aria-hidden
                                animate={{ rotate: 360 }}
                                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                                className='absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none'
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                                }}
                            />
                            <motion.div
                                animate={{ rotate: [0, 8, -8, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                className='inline-flex w-12 h-12 md:w-14 md:h-14 rounded-2xl items-center justify-center mb-3'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    boxShadow:
                                        "0 12px 28px -8px rgba(127,0,255,0.55)",
                                }}
                            >
                                <HiSparkles className='text-2xl text-white' />
                            </motion.div>
                            <h3 className='relative text-client-font font-bold text-xl md:text-2xl tracking-tight'>
                                Liked this read?
                            </h3>
                            <p className='relative text-client-font/65 text-sm md:text-base mt-2 mb-5 max-w-md mx-auto'>
                                Explore more stories and creative insights on the blog.
                            </p>
                            <div className='relative flex flex-wrap justify-center gap-2'>
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => navigate("/blog")}
                                    className='inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        boxShadow:
                                            "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                                    }}
                                >
                                    <FiBookOpen />
                                    More articles
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={handleShare}
                                    className='ios-glass-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-client-font text-sm font-medium'
                                >
                                    {copied ? (
                                        <>
                                            <FiCheck style={{ color: "#FCFA02" }} />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <FiShare2 />
                                            Share
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </motion.div>

                        {/* Footer info */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            className='pt-2 flex flex-wrap items-center justify-between gap-3 text-client-font/55 text-xs md:text-sm'
                        >
                            <div>
                                <p>Published: {formatDate(blog.createdAt)}</p>
                                {blog.updatedAt && blog.updatedAt !== blog.createdAt && (
                                    <p>Updated: {formatDate(blog.updatedAt)}</p>
                                )}
                            </div>
                            <div className='inline-flex items-center gap-1.5'>
                                <FiEye style={{ color: "#FCFA02" }} />
                                <span className='tabular-nums'>{formatViews(blog.views)}</span>
                                <span>views</span>
                            </div>
                        </motion.div>
                    </article>
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default ClientBlogInfo;
